import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Crown, Database, PenTool, BarChart, Headset } from 'lucide-react';

const PremiumGateOverlay = ({ onClose }) => {
    const { t } = useTranslation();

    const features = [
        {
            icon: Database,
            title: t('analytics.premiumPromotion.featureStorage'),
            desc: t('analytics.premiumPromotion.featureStorageDesc'),
            color: 'text-blue-500',
            bg: 'bg-blue-500/10'
        },
        {
            icon: PenTool,
            title: t('analytics.premiumPromotion.featureDesigner'),
            desc: t('analytics.premiumPromotion.featureDesignerDesc'),
            color: 'text-purple-500',
            bg: 'bg-purple-500/10'
        },
        {
            icon: BarChart,
            title: t('analytics.premiumPromotion.featureStats'),
            desc: t('analytics.premiumPromotion.featureStatsDesc'),
            color: 'text-amber-500',
            bg: 'bg-amber-500/10'
        },
        {
            icon: Headset,
            title: t('analytics.premiumPromotion.featureSupport'),
            desc: t('analytics.premiumPromotion.featureSupportDesc'),
            color: 'text-green-500',
            bg: 'bg-green-500/10'
        }
    ];

    return (
        <div className="w-full h-full bg-[#0a0a0b] text-white flex flex-col relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-purple-900/10 to-transparent pointer-events-none" />

            {/* Main Content Container - Centered Vertically & Horizontally */}
            <div className="flex-1 flex flex-col items-center justify-center p-8 w-full max-w-4xl mx-auto h-full space-y-6">

                {/* Header Section */}
                <div className="text-center space-y-4">
                    <div className="w-20 h-20 rounded-3xl bg-gradient-to-tr from-amber-400 to-amber-600 flex items-center justify-center mx-auto shadow-[0_0_40px_rgba(251,191,36,0.2)] animate-pulse">
                        <Crown className="w-10 h-10 text-white" />
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
                            {t('analytics.premiumPromotion.title')}
                        </h2>
                        <p className="text-lg text-gray-400 font-light">
                            {t('analytics.premiumPromotion.subtitle')}
                        </p>
                    </div>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-3xl">
                    {features.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                            <div
                                key={index}
                                className="p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-all duration-300 group hover:-translate-y-1"
                            >
                                <div className="flex items-start gap-3">
                                    <div className={`p-2 rounded-lg ${feature.bg} ${feature.color} group-hover:scale-110 transition-transform`}>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div className="space-y-1">
                                        <h3 className="font-bold text-white text-base">{feature.title}</h3>
                                        <p className="text-sm text-gray-400 leading-relaxed">
                                            {feature.desc}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer/Action Section */}
                <div className="w-full max-w-3xl pt-2">
                    <div className="p-6 rounded-2xl bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/10 flex flex-col md:flex-row items-center justify-between gap-4 backdrop-blur-sm">
                        <div className="text-center md:text-left">
                            <p className="text-white font-medium text-base">
                                {t('analytics.premiumPromotion.contactForUpgrade')}
                            </p>
                            <p className="text-gray-400 text-xs mt-0.5">
                                {t('analytics.premiumPromotion.supportTeamReady', 'Our team is ready to help you upgrade.')}
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                            <Button
                                className="bg-white text-black hover:bg-gray-200 font-bold h-10 px-6 text-sm rounded-lg"
                                onClick={() => window.open('mailto:info@snapp.ai')}
                            >
                                info@snapp.ai
                            </Button>
                            <Button
                                variant="outline"
                                className="border-gray-700 text-white hover:bg-white/5 font-bold h-10 px-6 text-sm rounded-lg"
                                onClick={onClose}
                            >
                                {t('analytics.premiumPromotion.close')}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PremiumGateOverlay;
